import { useEffect } from "react";
import { Link } from "wouter";
import { blogPosts } from "@/data/blog-posts";

const TEAL = "#6FAFB5";
const NAVY = "#1F2E3B";
const CREAM = "#F7F4EE";
const MUTED = "#536270";
const GOLD = "#E5B75E";

function Nav() {
  return (
    <nav style={{ background: "rgba(247,244,238,.92)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
      <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
        <Link href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <svg width="28" height="16" viewBox="0 0 28 16" fill="none" aria-hidden="true">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", letterSpacing: "-0.01em", color: NAVY, lineHeight: 1 }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
        </Link>
        <div className="flex items-center gap-6">
          <Link href="/" style={{ fontSize: "0.85rem", color: MUTED, textDecoration: "none" }}>← Back to BizDr</Link>
          <Link href="/check"
            style={{ background: NAVY, color: "#fff", textDecoration: "none", borderRadius: 999, padding: "8px 20px", fontSize: "0.85rem", fontWeight: 500 }}>
            Book a BizDr Check
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer style={{ background: NAVY, padding: "2rem 1.75rem", marginTop: "auto" }}>
      <div className="mx-auto max-w-6xl flex flex-wrap items-center justify-between gap-4">
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", color: "#fff" }}>
          Biz<em style={{ fontStyle: "italic", color: TEAL }}>Dr</em>
        </span>
        <div className="flex gap-5 flex-wrap">
          <Link href="/" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Home</Link>
          <Link href="/blog" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Blog</Link>
          <Link href="/privacy" style={{ fontSize: "0.8rem", color: "rgba(255,255,255,.4)", textDecoration: "none" }}>Privacy</Link>
        </div>
        <span style={{ fontSize: "0.75rem", color: "rgba(255,255,255,.3)" }}>© {year} BizDr. All rights reserved. · ABN 69 491 275 460</span>
      </div>
    </footer>
  );
}

export default function Blog() {
  useEffect(() => {
    document.title = "Free Resources for Small Business | BizDr";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Free guides and tips to help small Australian businesses get found online, build credibility, and grow without wasting money.");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", "Free Resources for Small Business | BizDr");
  }, []);

  return (
    <div style={{ background: CREAM, minHeight: "100vh", fontFamily: "'DM Sans', sans-serif", color: NAVY, display: "flex", flexDirection: "column" }}>
      <Nav />

      <main style={{ flex: 1 }}>
        {/* Hero */}
        <section style={{ background: NAVY, padding: "4rem 1.75rem 3rem" }}>
          <div className="mx-auto max-w-6xl">
            <div style={{ fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.1em", color: GOLD, marginBottom: "1rem" }}>
              Free Resources
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, color: "#fff", marginBottom: "1rem", lineHeight: 1.2 }}>
              Real answers for small business owners.
            </h1>
            <p style={{ color: "rgba(255,255,255,.65)", fontSize: "1.05rem", maxWidth: 560, lineHeight: 1.7 }}>
              No jargon, no fluff. Just practical guides to help you get found online, look credible, and grow without wasting money.
            </p>
          </div>
        </section>

        {/* Post grid */}
        <section style={{ padding: "3rem 1.75rem" }}>
          <div className="mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {blogPosts.map((post, i) => (
                <Link key={post.slug} href={`/blog/${post.slug}`} style={{ textDecoration: "none" }}>
                  <article
                    data-testid={`card-blog-${i}`}
                    style={{
                      background: "#fff",
                      border: "1px solid #e4ddd2",
                      borderRadius: 20,
                      padding: "1.75rem",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      cursor: "pointer",
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)"; (e.currentTarget as HTMLElement).style.boxShadow = "0 8px 24px rgba(0,0,0,.07)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ""; (e.currentTarget as HTMLElement).style.boxShadow = ""; }}
                  >
                    <div style={{ fontSize: "0.7rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: TEAL }}>
                      Free Guide
                    </div>
                    <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.1rem", fontWeight: 500, color: NAVY, lineHeight: 1.35, margin: 0 }}>
                      {post.title}
                    </h2>
                    <p style={{ fontSize: "0.875rem", color: MUTED, lineHeight: 1.65, margin: 0, flex: 1 }}>
                      {post.summary}
                    </p>
                    <div style={{ fontSize: "0.8rem", color: TEAL, fontWeight: 500, marginTop: "0.5rem" }}>
                      Read more →
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section style={{ background: "#fff", borderTop: "1px solid #e4ddd2", padding: "3rem 1.75rem" }}>
          <div className="mx-auto max-w-2xl text-center">
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.6rem", fontWeight: 500, color: NAVY, marginBottom: "0.75rem" }}>
              Ready to actually do something about it?
            </h2>
            <p style={{ color: MUTED, marginBottom: "1.5rem", lineHeight: 1.7 }}>
              A free BizDr Check takes 5 minutes and tells you exactly where your business stands online.
            </p>
            <Link href="/check"
              style={{ display: "inline-block", background: TEAL, color: "#fff", textDecoration: "none", borderRadius: 999, padding: "14px 32px", fontWeight: 500 }}>
              Get a Free BizDr Check
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
